import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { Badge } from '@components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import { auditService } from '@services/audit.service'
import { useQuery } from '@tanstack/react-query'
import { Shield, FileText, User, Building, AlertCircle } from 'lucide-react'

export function AuditLogsPage() {
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    contains_phi: undefined as boolean | undefined,
    start_date: '',
    end_date: '',
  })

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => auditService.getAuditLogs({
      entity_type: filters.entity_type || undefined,
      action: filters.action || undefined,
      contains_phi: filters.contains_phi,
      start_date: filters.start_date || undefined,
      end_date: filters.end_date || undefined,
      limit: 50,
    }),
  })

  const { data: phiReport } = useQuery({
    queryKey: ['phi-access-report'],
    queryFn: () => auditService.getPHIAccessReport(),
  })

  const entityTypes = ['Patient', 'User', 'Appointment', 'Prescription', 'Document', 'Billing']
  const actions = ['CREATE', 'READ', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'EXPORT']

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Registros de Auditoría</h1>
          <p className="text-muted-foreground">Monitorear acceso al sistema y cumplimiento</p>
        </div>
        <Button onClick={() => window.print()}>
          <FileText className="mr-2 h-4 w-4" />
          Exportar Informe
        </Button>
      </div>

      {/* PHI Access Summary */}
      {phiReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Resumen de Acceso a IPS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold">{phiReport.total_phi_access}</div>
                <div className="text-sm text-muted-foreground">Total Acceso a IPS</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold">{phiReport.users.length}</div>
                <div className="text-sm text-muted-foreground">Usuarios Únicos</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold">{phiReport.entities.length}</div>
                <div className="text-sm text-muted-foreground">Entidades Accedidas</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs">Registros de Auditoría</TabsTrigger>
          <TabsTrigger value="phi">Acceso a IPS</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Filtros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Select
                  value={filters.entity_type}
                  onValueChange={(value) => setFilters({ ...filters, entity_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo de Entidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos los Tipos</SelectItem>
                    {entityTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.action}
                  onValueChange={(value) => setFilters({ ...filters, action: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Acción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todas las Acciones</SelectItem>
                    {actions.map((action) => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.contains_phi?.toString() || ''}
                  onValueChange={(value) => setFilters({ 
                    ...filters, 
                    contains_phi: value === '' ? undefined : value === 'true' 
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Solo IPS" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos</SelectItem>
                    <SelectItem value="true">Solo IPS</SelectItem>
                    <SelectItem value="false">Solo No-IPS</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="date"
                  placeholder="Fecha de Inicio"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />

                <Input
                  type="date"
                  placeholder="Fecha de Fin"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Audit Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle>Pista de Auditoría</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">Cargando...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha y Hora</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Acción</TableHead>
                      <TableHead>Entidad</TableHead>
                      <TableHead>Detalles</TableHead>
                      <TableHead>IPS</TableHead>
                      <TableHead>Dirección IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditData?.logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-sm">
                          {new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {log.user_id}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={log.action === 'DELETE' ? 'destructive' : 'default'}>
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            {log.entity_type}: {log.entity_id?.substring(0, 8)}...
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                          {JSON.stringify(log.details).substring(0, 50)}...
                        </TableCell>
                        <TableCell>
                          {log.contains_phi ? (
                            <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                              <AlertCircle className="h-3 w-3" />
                              Sí
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="w-fit">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{log.ip_address}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!isLoading && auditData?.logs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Sin registros de auditoría encontrados
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="phi">
          <Card>
            <CardHeader>
              <CardTitle>Acceso a IPS por Usuario</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Cantidad de Accesos</TableHead>
                    <TableHead>Último Acceso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {phiReport?.users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.phi_access_count}</Badge>
                      </TableCell>
                      <TableCell>{new Date(user.last_phi_access).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
